import { useLocation, useNavigate } from "react-router-dom";
import { createStyles, UnstyledButton, Text, Tooltip } from "@mantine/core";
import {
  IconApps,
  IconUsers,
  IconSettings,
  IconBrandGithub,
  IconCloudUp,
  IconKey,
  IconShieldLock,
  IconRobot,
  IconFiles,
} from "@tabler/icons-react";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from "../types";

const useStyles = createStyles((theme) => {
  const dark = theme.colorScheme === "dark";
  return {
    button: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      paddingLeft: 20,
      paddingRight: 16,
      height: 44,
      marginBottom: 4,
      borderRadius: 8,
      color: dark ? "#a0a0a0" : "#616161",
      fontWeight: 500,
      transition: "background-color 0.15s ease, color 0.15s ease",

      // Fixed icon column for clean label alignment (matches thanos)
      "& svg": {
        marginRight: 14,
        flexShrink: 0,
      },

      "&:hover": {
        backgroundColor: theme.fn.rgba(theme.colors.thanos[5], dark ? 0.08 : 0.04),
      },
    },

    collapsed: {
      justifyContent: "center",
      paddingLeft: 0,
      paddingRight: 0,
      "& svg": {
        marginRight: 0,
      },
    },

    active: {
      backgroundColor: theme.fn.rgba(theme.colors.thanos[5], dark ? 0.12 : 0.08),
      color: theme.colors.thanos[5],
      fontWeight: 600,

      "&:hover": {
        backgroundColor: theme.fn.rgba(theme.colors.thanos[5], dark ? 0.16 : 0.1),
      },
    },

    sectionLabel: {
      padding: `0 ${theme.spacing.sm}px`,
      marginTop: 18,
      marginBottom: 8,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: dark ? "#6b6b6b" : "#9ca3af",
    },
  };
});

interface MainLinkProps {
  icon: React.ReactNode;
  color: string;
  label: string;
  path: string;
  collapsed?: boolean;
}

function MainLink({ icon, label, path, collapsed }: MainLinkProps) {
  const { classes, cx } = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const isActive =
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const button = (
    <UnstyledButton
      className={cx(classes.button, {
        [classes.active]: isActive,
        [classes.collapsed]: collapsed,
      })}
      onClick={() => navigate(path)}
    >
      {icon}
      {!collapsed && <Text size="sm">{label}</Text>}
    </UnstyledButton>
  );

  if (collapsed) {
    return (
      <Tooltip label={label} position="right">
        {button}
      </Tooltip>
    );
  }

  return button;
}

interface MainLinksProps {
  collapsed?: boolean;
}

export function MainLinks({ collapsed }: MainLinksProps) {
  const { user } = useAuth();
  const isAdmin =
    user?.role === UserRole.ADMIN || user?.role === UserRole.SUPERUSER;
  const isSuperuser = user?.role === UserRole.SUPERUSER;

  // Links available to every authenticated user
  const userLinks = [
    {
      icon: <IconApps size={20} />,
      color: "blue",
      label: "Services",
      path: "/services",
      show: true,
    },
    {
      icon: <IconBrandGithub size={20} />,
      color: "dark",
      label: "GitHub Access",
      path: "/github-access",
      show: true,
    },
    {
      icon: <IconCloudUp size={20} />,
      color: "teal",
      label: "Deployments",
      path: "/deployments",
      show: true,
    },
    {
      icon: <IconRobot size={20} />,
      color: "grape",
      label: "Herald",
      path: "/herald",
      show: true,
    },
    {
      icon: <IconFiles size={20} />,
      color: "blue",
      label: "Files",
      path: "/files",
      show: true,
    },
  ];

  // Rendered as the last nav item
  const settingsLink = {
    icon: <IconSettings size={20} />,
    color: "gray",
    label: "Settings",
    path: "/settings",
    show: true,
  };

  // Administration links — shown directly based on the user's role
  const adminLinks = [
    {
      icon: <IconBrandGithub size={20} />,
      color: "orange",
      label: "GitHub",
      path: "/admin/github-access-requests",
      show: isAdmin,
    },
    {
      icon: <IconUsers size={20} />,
      color: "grape",
      label: "Users",
      path: "/admin/users",
      show: isAdmin,
    },
    {
      icon: <IconKey size={20} />,
      color: "teal",
      label: "Access Requests",
      path: "/admin/access-requests",
      show: isAdmin,
    },
    {
      icon: <IconShieldLock size={20} />,
      color: "red",
      label: "Keycloak",
      path: "/admin/keycloak",
      show: isSuperuser,
    },
  ];

  const visibleAdminLinks = adminLinks.filter((link) => link.show);

  return (
    <div>
      {userLinks
        .filter((link) => link.show)
        .map((link) => (
          <MainLink {...link} collapsed={collapsed} key={link.label} />
        ))}

      {visibleAdminLinks.map((link) => (
        <MainLink {...link} collapsed={collapsed} key={link.label} />
      ))}

      {/* Settings as the last item */}
      <MainLink {...settingsLink} collapsed={collapsed} />
    </div>
  );
}
